import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  TextField,
  MenuItem,
  Button,
  Typography,
  IconButton,
  CircularProgress,
  Chip,
  Tooltip,
  Divider,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import UploadFileOutlinedIcon from "@mui/icons-material/UploadFileOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import Swal from "sweetalert2";
import { AssetFinanceUrl } from "../../../../../config";
import {
  executeAnyMutation,
  fetchJsonDynamic,
} from "../../../../../data/DynamicServiceNew/jsondynamicservice";
import { NumericFormat } from "react-number-format";

const base_url = AssetFinanceUrl.uri;

// ─────────────────────────────────────────────────────────────
// FILE STORAGE CONFIG
// ─────────────────────────────────────────────────────────────
const FILE_GATEWAY_URL = "https://api.sheriapro.sababisha.com";
const FILE_STORAGE_API_KEY =
  "E3VF4C6KFV43JH6GKCDFGJP45V36JHGV3H4C6F3GJC63HG405GH6V345GHHJ4623FJJL3HCVMO1P23PZ07W8";

const uploadFileToStorage = async (
  file,
  project = "maziwa",
  folder = "valuation",
) => {
  const form = new FormData();
  form.append("file", file);
  form.append("project", project);
  form.append("folder", folder);

  const response = await fetch(
    `${FILE_GATEWAY_URL}/filestorageservice/upload`,
    {
      method: "POST",
      headers: {
        "X-Api-Key": FILE_STORAGE_API_KEY,
      },
      body: form,
    },
  );

  if (!response.ok) throw new Error(`Upload failed (${response.status})`);
  return await response.json();
};

const deleteFileFromStorage = async (objectName) => {
  await fetch(`${FILE_GATEWAY_URL}/filestorageservice/delete`, {
    method: "DELETE",
    headers: {
      "Content-Type": "application/json",
      "X-Api-Key": FILE_STORAGE_API_KEY,
    },
    body: JSON.stringify({ ObjectName: objectName }),
  });
};

// ─────────────────────────────────────────────────────────────

const VALUATION_METHODS = [
  { value: "MARKET", label: "Market Approach" },
  { value: "COMPARABLE_SALES", label: "Comparable Sales Approach" },
  {
    value: "DEPRECIATED_REPLACEMENT",
    label: "Depreciated Replacement Cost Approach",
  },
  { value: "INCOME", label: "Income Approach" },
];

const todayIso = () => new Date().toISOString().split("T")[0];

const parseResults = (response) => {
  if (!response?.results) return [];
  try {
    return JSON.parse(response.results) || [];
  } catch {
    return [];
  }
};

export const CollateralValuationDialog = ({
  open,
  onClose,
  Row,
  onSuccess,
}) => {
  const decodedToken = JSON.parse(localStorage.getItem("decodedToken"));
  const [submitting, setSubmitting] = useState(false);
  const [valuerFirms, setValuerFirms] = useState([]);
  const [loadingValuerFirms, setLoadingValuerFirms] = useState(true);
  const [form, setForm] = useState({
    valuerFirmId: "",
    valuationMethod: VALUATION_METHODS[0].value,
    openMarketValue: "",
    forcedSaleValue: "",
    valuationDate: todayIso(),
    validUntil: "",
  });
  const [errors, setErrors] = useState({});

  // ── Free-form document upload state ──
  const [stagedFiles, setStagedFiles] = useState([]);
  const [existingDocuments, setExistingDocuments] = useState([]);
  const [fileError, setFileError] = useState(null);
  const [deletingDocId, setDeletingDocId] = useState(null);

  // Load valuer firms
  useEffect(() => {
    (async () => {
      const response = await fetchJsonDynamic(
        "allValuerFirms",
        null,
        null,
        base_url,
      );
      const results = response?.results ? JSON.parse(response.results) : [];
      setValuerFirms(results);
      setLoadingValuerFirms(false);
    })();
  }, []);

  // Load existing documents using valuationDocumentByColumnValues (same pattern as collateralAssetDocumentByColumnValues)
  useEffect(() => {
    if (!open || !Row?.Id) return;

    (async () => {
      try {
        const res = await fetchJsonDynamic(
          "valuationDocumentByColumnValues",
          "valuationDocument",
          { assetId: Number(Row.Id) },
          base_url,
        );

        const results = parseResults(res);

        setExistingDocuments(results);

        if (results.length > 0) {
          const valuation = results[0];

          setForm({
            valuerFirmId: Number(valuation.ValuerFirmId) || "",
            valuationMethod:
              valuation.ValuationMethod ?? VALUATION_METHODS[0].value,
            openMarketValue: valuation.OpenMarketValue ?? "",
            forcedSaleValue: valuation.ForcedSaleValue ?? "",
            valuationDate: valuation.ValuationDate
              ? valuation.ValuationDate.split("T")[0]
              : todayIso(),
            validUntil: valuation.ValidUntil
              ? valuation.ValidUntil.split("T")[0]
              : "",
          });
        }
      } catch {
        setExistingDocuments([]);
      }
    })();
  }, [open, Row?.Id]);

  const setField = (name, value) => {
    setForm((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: undefined }));
  };
  const getPresignedUrl = async (objectName, expirySeconds = 3600) => {
    const response = await fetch(
      `${FILE_GATEWAY_URL}/filestorageservice/presigned-url?objectName=${encodeURIComponent(objectName)}&expiry=${expirySeconds}`,
      {
        method: "GET",
        headers: {
          "X-Api-Key": FILE_STORAGE_API_KEY,
        },
      },
    );

    if (!response.ok) throw new Error("Failed to get download URL");

    const data = await response.json();
    return data.url;
  };

  const validate = () => {
    const nextErrors = {};
    if (!form.valuerFirmId) nextErrors.valuerFirmId = "Valuer firm is required";
    if (!form.openMarketValue)
      nextErrors.openMarketValue = "Open market value is required";
    if (!form.forcedSaleValue)
      nextErrors.forcedSaleValue = "Forced sale value is required";
    if (!form.valuationDate)
      nextErrors.valuationDate = "Valuation date is required";
    if (!form.validUntil) nextErrors.validUntil = "Validity date is required";
    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  // ── Document helpers ──
  const handleDocumentSelect = (file) => {
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) {
      setFileError("File exceeds the 8MB limit");
      return;
    }
    setStagedFiles((prev) => [
      ...prev,
      { file, id: Date.now() + Math.random() },
    ]);
    setFileError(null);
  };

  const handleRemoveStaged = (stagedId) => {
    setStagedFiles((prev) => prev.filter((s) => s.id !== stagedId));
  };

  const handleDeleteExisting = async (doc) => {
    const confirm = await Swal.fire({
      icon: "warning",
      title: "Delete document?",
      text: `This will permanently delete "${doc.FileName || doc.DocumentName}" from storage.`,
      showCancelButton: true,
      confirmButtonText: "Yes, delete it",
      confirmButtonColor: "#d33",
    });
    if (!confirm.isConfirmed) return;

    setDeletingDocId(doc.Id);
    try {
      await deleteFileFromStorage(doc.ObjectName);
      await executeAnyMutation(
        "deleteValuationDocument",
        { Id: Number(doc.Id), ObjectName: doc.ObjectName },
        "valuationDocument",
        false,
        base_url,
      );
      setExistingDocuments((prev) => prev.filter((d) => d.Id !== doc.Id));
    } catch {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Could not delete document. Try again.",
      });
    } finally {
      setDeletingDocId(null);
    }
  };

  const uploadStagedDocuments = async (valuationId) => {
    const failures = [];
    for (const staged of stagedFiles) {
      try {
        const minioResult = await uploadFileToStorage(
          staged.file,
          "maziwa",
          "valuation",
        );
        const saveResult = await executeAnyMutation(
          "saveValuationDocument",
          {
            id: Number(valuationId),
            assetId: Number(Row?.Id),
            valuerFirmId: Number(form.valuerFirmId),
            forcedSaleValue: parseFloat(form.forcedSaleValue),
            openMarketValue: parseFloat(form.openMarketValue),
            valuationMethod: form.valuationMethod,
            valuationDate: form.valuationDate,
            validUntil: form.validUntil,
            status: "SUBMITTED",
            companyId:
              Number(Row?.CompanyId) || parseInt(decodedToken?.CompanyId),
            createdBy: parseInt(decodedToken?.Id),
            documentName: staged.file.name,
            fileName: minioResult.fileName,
            objectName: minioResult.objectName,
            url: minioResult.url,
            mimeType: staged.file.type,
            fileSizeBytes: staged.file.size,
          },
          "valuationDocument",
          false,
          base_url,
        );

        if (saveResult?.error) {
          failures.push(staged.file.name);
        }
      } catch (err) {
        failures.push(staged.file.name);
      }
    }
    return failures;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    setSubmitting(true);

    try {
      const assetValuation = {
        assetId: Number(Row?.Id),
        companyId: Number(Row?.CompanyId) || parseInt(decodedToken?.CompanyId),
        valuerFirmId: Number(form.valuerFirmId),
        valuationMethod: form.valuationMethod,
        openMarketValue: parseFloat(form.openMarketValue),
        forcedSaleValue: parseFloat(form.forcedSaleValue),
        valuationDate: form.valuationDate,
        validUntil: form.validUntil,
        status: "SUBMITTED",
      };

      const response = await executeAnyMutation(
        "createAssetValuation",
        assetValuation,
        "assetValuation",
        false,
        base_url,
      );

      if (response?.results) {
        let savedValuation = null;
        try {
          savedValuation = JSON.parse(response.results);
        } catch {
          savedValuation = null;
        }
        const valuationId = savedValuation?.Id;

        // Upload staged documents
        const failedUploads = valuationId
          ? await uploadStagedDocuments(valuationId)
          : stagedFiles.map((s) => s.file.name);

        onSuccess?.();
        onClose();

        if (failedUploads.length > 0) {
          Swal.fire({
            icon: "warning",
            title: "Valuation submitted",
            html: `Saved successfully, but these documents failed to upload — re-attach from the edit screen:<br/><b>${failedUploads.join(
              ", ",
            )}</b>`,
          });
        } else {
          Swal.fire({
            icon: "success",
            title: "Success!",
            text: "Valuation submitted successfully.",
          });
        }
      } else if (response?.error) {
        let errorMessage = "An unknown error occurred.";
        try {
          const errorObj =
            typeof response.error === "string"
              ? JSON.parse(response.error)
              : response.error;
          errorMessage =
            (typeof errorObj === "string" && errorObj) ||
            errorObj?.error ||
            errorObj?.Error?.match(/System\.Exception: (.*?)\./)?.[1] ||
            errorObj?.Error ||
            errorMessage;
        } catch {
          errorMessage = response.error;
        }
        Swal.fire({
          icon: "error",
          title: "Error!",
          text: "Please contact the Admin.",
        });
      }
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Error!",
        text: "An error occurred while submitting the valuation. Please try again.",
      });
    }
    setSubmitting(false);
  };
  const handleViewDocument = async (doc) => {
    if (!doc?.ObjectName) return;

    try {
      const url = await getPresignedUrl(doc.ObjectName);
      window.open(url, "_blank");
    } catch {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Could not generate download link. Please try again.",
      });
    }
  };
  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        Record Asset Valuation
        <IconButton size="small" onClick={onClose}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" color="text.secondary" mb={2}>
          Recording a valuation for collateral asset #{Row?.Id}
        </Typography>
        <Box
          display="grid"
          gap="16px"
          gridTemplateColumns="repeat(2, minmax(0, 1fr))"
        >
          <TextField
            select
            fullWidth
            variant="filled"
            label="Valuer Firm *"
            value={form.valuerFirmId}
            onChange={(e) => setField("valuerFirmId", e.target.value)}
            disabled={loadingValuerFirms}
            error={!!errors.valuerFirmId}
            helperText={
              errors.valuerFirmId ||
              (loadingValuerFirms ? "Loading valuer firms…" : " ")
            }
            sx={{ gridColumn: "span 2" }}
          >
            {valuerFirms.map((firm) => (
              <MenuItem key={firm.Id} value={firm.Id}>
                {firm.Name}
                {firm.RegistrationNo ? ` (${firm.RegistrationNo})` : ""}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            fullWidth
            variant="filled"
            label="Valuation Method *"
            value={form.valuationMethod}
            onChange={(e) => setField("valuationMethod", e.target.value)}
            sx={{ gridColumn: "span 2" }}
          >
            {VALUATION_METHODS.map((method) => (
              <MenuItem key={method.value} value={method.value}>
                {method.label}
              </MenuItem>
            ))}
          </TextField>
          <NumericFormat
            customInput={TextField}
            fullWidth
            variant="filled"
            label="Open Market Value (KES) *"
            thousandSeparator
            decimalScale={2}
            fixedDecimalScale
            allowNegative={false}
            value={form.openMarketValue}
            onValueChange={(values) => {
              setField("openMarketValue", values.floatValue ?? "");
            }}
            error={!!errors.openMarketValue}
            helperText={errors.openMarketValue}
          />
          <NumericFormat
            customInput={TextField}
            fullWidth
            variant="filled"
            label="Forced Sale Value (KES) *"
            value={form.forcedSaleValue}
            thousandSeparator
            decimalScale={2}
            fixedDecimalScale
            allowNegative={false}
            onValueChange={(values) => {
              setField("forcedSaleValue", values.floatValue ?? "");
            }}
            error={!!errors.forcedSaleValue}
            helperText={errors.forcedSaleValue}
          />
          <TextField
            fullWidth
            variant="filled"
            type="date"
            label="Valuation Date *"
            InputLabelProps={{ shrink: true }}
            value={form.valuationDate}
            onChange={(e) => setField("valuationDate", e.target.value)}
            error={!!errors.valuationDate}
            helperText={errors.valuationDate}
          />
          <TextField
            fullWidth
            variant="filled"
            type="date"
            label="Valid Until *"
            InputLabelProps={{ shrink: true }}
            value={form.validUntil}
            onChange={(e) => setField("validUntil", e.target.value)}
            error={!!errors.validUntil}
            helperText={errors.validUntil}
          />
        </Box>

        {/* ── FREE-FORM DOCUMENT UPLOAD SECTION ── */}
        <Box mt={3}>
          <Divider sx={{ mb: 2 }} />
          <Typography variant="subtitle1" fontWeight={700} mb={2}>
            Supporting Documents
          </Typography>

          {/* Upload button */}
          <Button
            component="label"
            variant="outlined"
            color="primary"
            startIcon={<UploadFileOutlinedIcon />}
            sx={{ mb: 2 }}
          >
            Upload Document
            <input
              type="file"
              hidden
              accept="image/*,.pdf,.doc,.docx,.xls,.xlsx"
              onChange={(e) => handleDocumentSelect(e.target.files[0])}
            />
          </Button>

          {fileError && (
            <Typography
              variant="caption"
              color="error"
              sx={{ display: "block", mb: 1 }}
            >
              {fileError}
            </Typography>
          )}

          {/* Existing documents (from DB) */}
          {existingDocuments.length > 0 && (
            <Box mb={2}>
              <Typography
                variant="body2"
                fontWeight={600}
                color="text.secondary"
                mb={1}
              >
                Saved Documents
              </Typography>
              {existingDocuments.map((doc) => (
                <Box
                  key={doc.Id}
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                  sx={{
                    py: 1,
                    px: 1.5,
                    mb: 0.5,
                    borderRadius: 1,
                    bgcolor: "rgba(255,255,255,0.03)",
                    border: "1px solid rgba(255,255,255,0.06)",
                  }}
                >
                  <Box display="flex" alignItems="center" gap={1}>
                    <DescriptionOutlinedIcon
                      fontSize="small"
                      sx={{ color: "rgba(255,255,255,0.5)" }}
                    />
                    <Chip
                      icon={
                        <CheckCircleIcon sx={{ fontSize: "16px !important" }} />
                      }
                      label={doc.DocumentName || "Unnamed Document"}
                      size="small"
                      color="success"
                      variant="outlined"
                      sx={{
                        maxWidth: 220,
                        cursor: "pointer",
                      }}
                      clickable
                      onClick={() => handleViewDocument(doc)}
                    />
                  </Box>
                  <Tooltip title="Delete saved document">
                    <span>
                      <IconButton
                        size="small"
                        disabled={deletingDocId === doc.Id}
                        onClick={() => handleDeleteExisting(doc)}
                      >
                        {deletingDocId === doc.Id ? (
                          <CircularProgress size={16} />
                        ) : (
                          <DeleteOutlineIcon fontSize="small" color="error" />
                        )}
                      </IconButton>
                    </span>
                  </Tooltip>
                </Box>
              ))}
            </Box>
          )}

          {/* Staged documents (not yet uploaded) */}
          {stagedFiles.length > 0 && (
            <Box>
              <Typography
                variant="body2"
                fontWeight={600}
                color="info.main"
                mb={1}
              >
                Pending Upload
              </Typography>
              {stagedFiles.map((staged) => (
                <Box
                  key={staged.id}
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                  sx={{
                    py: 1,
                    px: 1.5,
                    mb: 0.5,
                    borderRadius: 1,
                    bgcolor: "rgba(33,150,243,0.05)",
                    border: "1px solid rgba(33,150,243,0.15)",
                  }}
                >
                  <Box display="flex" alignItems="center" gap={1}>
                    <DescriptionOutlinedIcon
                      fontSize="small"
                      sx={{ color: "info.main" }}
                    />
                    <Chip
                      label={staged.file.name}
                      size="small"
                      color="info"
                      variant="outlined"
                      sx={{ maxWidth: 220 }}
                    />
                  </Box>
                  <Tooltip title="Remove">
                    <IconButton
                      size="small"
                      onClick={() => handleRemoveStaged(staged.id)}
                    >
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              ))}
            </Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose} variant="outlined">
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          color="secondary"
          disabled={submitting}
        >
          {submitting ? <CircularProgress size={20} /> : "Submit Valuation"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CollateralValuationDialog;
